// Customer case studies — single source of truth for /case-studies (index), each
// /case-studies/<slug> page (prerendered via getStaticPaths in routes.tsx) and the
// "Read the case study" link on /customers.
//
// Every claim here is published with the customer's permission and must match what
// is actually deployed. No invented metrics or quotes: add a `quote` only once the
// customer has approved its exact wording.

import { vadenChiltonQuote } from './customerQuotes';

export interface CaseStudyQuote {
  quote: string;
  author: string;
  title: string;
  linkedIn?: string;
  // When the person signs with a different company name than `customer`.
  company?: string;
  photo?: string;
  photoSize?: number;
  // Our Portuguese translation, shown under the quote on gizmodata.com.br (src/components/QuoteGloss.tsx).
  ptBR?: string;
}

export interface CaseStudySection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
  link?: { to: string; label: string; cta: string };
}

export interface CaseStudy {
  slug: string;
  customer: string;
  // Full-color logos on transparent backgrounds; render them on a light plate.
  logoHorizontal: string;
  logoVertical: string;
  logoWidth: number;
  logoHeight: number;
  logoVerticalWidth: number;
  logoVerticalHeight: number;
  customerUrl: string;
  customerLinkedIn?: string;
  customerDescription: string;
  industry: string;
  headquarters: string;
  deployment: string;
  products: string[];
  title: string;
  summary: string;
  // Card + SEO copy.
  excerpt: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string;
  published: string;
  sections: CaseStudySection[];
  faqs: { question: string; answer: string }[];
  quote?: CaseStudyQuote;
}

export const caseStudies: CaseStudy[] = [
  {
    slug: 'vaden-automotive',
    customer: 'Vaden Automotive',
    logoHorizontal: '/customers/vaden-automotive-horizontal.png',
    logoVertical: '/customers/vaden-automotive-vertical.png',
    logoWidth: 1248,
    logoHeight: 386,
    logoVerticalWidth: 1228,
    logoVerticalHeight: 831,
    customerUrl: 'https://www.danvaden.com',
    customerLinkedIn: 'https://www.linkedin.com/company/vadenautomotive',
    customerDescription: 'Multi-franchise new and used car dealership group serving Savannah, Georgia and the South Carolina Lowcountry',
    industry: 'Automotive retail',
    headquarters: 'Savannah, Georgia',
    deployment: 'GizmoData Cloud at Customer on AWS',
    products: ['GizmoData Cloud at Customer', 'GizmoSQL Enterprise', 'GizmoSQL MCP Server', 'GizmoData App Factory (early access)'],
    title: 'Vaden Automotive gets faster reports and AI answers at a fraction of the cost with GizmoSQL',
    summary:
      'Vaden Automotive runs GizmoData Cloud at Customer inside its own AWS account. Data replicated from Snowflake into GizmoSQL powers two things: an MCP server that lets employees ask business questions in plain English through Claude, and internal real-time analytics apps built with the GizmoData App Factory. Reports and AI answers are faster, and costs have dropped dramatically.',
    excerpt:
      'A dealership group replicates Snowflake data into GizmoSQL in its own AWS account. Employees ask questions through Claude, internal apps show real-time data, and reports run faster for dramatically less money.',
    seoTitle: 'Vaden Automotive Case Study | Faster reports and AI answers on GizmoSQL | GizmoData',
    seoDescription:
      'How Vaden Automotive uses GizmoData Cloud at Customer on AWS: Snowflake data replicated into GizmoSQL serves natural-language questions from Claude over the GizmoSQL MCP server and real-time internal apps. Reports and AI answers are faster, costs are dramatically lower, and GizmoData Cloud at Customer shows the engine\u2019s CPU, memory and query metrics.',
    keywords:
      'GizmoSQL case study, Vaden Automotive, Snowflake to DuckDB, natural language SQL Claude, MCP server analytics, DuckDB MCP, Cloud at Customer AWS, low-latency SQL engine, real-time analytics apps, dealership analytics',
    published: '2026-10-01',
    sections: [
      {
        id: 'goal',
        heading: 'The goal: fast answers from data already in Snowflake',
        paragraphs: [
          'Vaden Automotive’s data lives in Snowflake. The team wanted employees to get answers from it in two ways: by asking questions in plain English through an AI assistant, and through internal web apps built for specific jobs.',
          'Both workloads send many small queries and need answers in a fraction of a second, and running all of them on the warehouse is slow and expensive. Vaden wanted a fast, low-cost SQL engine for them, running inside its own AWS account.',
        ],
      },
      {
        id: 'platform',
        heading: 'GizmoData Cloud at Customer, inside Vaden’s AWS account',
        paragraphs: [
          'The GizmoData Cloud at Customer control plane and Vaden’s GizmoSQL clusters run in Vaden’s own AWS account, on the Kubernetes (EKS) cluster that already hosts its internal apps. Data sits in Vaden’s S3 buckets, encrypted with Vaden’s own KMS keys.',
          'Data is replicated from Snowflake into GizmoSQL. Snowflake remains the system of record, and GizmoSQL, which is built on DuckDB and speaks Arrow Flight SQL, serves the interactive queries. The GizmoSQL clusters run on AWS Graviton nodes with local NVMe storage.',
        ],
        link: { to: '/cloud/at-customer', label: 'How Cloud at Customer works', cta: 'view_cloud_at_customer' },
      },
      {
        id: 'claude-mcp',
        heading: 'Plain-English questions through Claude',
        paragraphs: [
          'Employees ask business questions in Claude. Claude answers them by querying GizmoSQL through the GizmoSQL MCP server: it lists the catalogs, inspects the tables and runs SQL, then answers with the results.',
          'Because GizmoSQL answers quickly, Claude can run several queries for one question and still reply promptly.',
        ],
        bullets: [
          'Read-only by default: the MCP server will not run writes unless they are explicitly enabled.',
          'Row caps and query timeouts are enforced on the server, so one question cannot run away with the cluster.',
          'Each connection uses its own GizmoSQL credentials, so GizmoSQL’s permissions decide what Claude can see.',
        ],
        link: { to: '/gizmosql/mcp', label: 'About the GizmoSQL MCP Server', cta: 'view_mcp' },
      },
      {
        id: 'app-factory',
        heading: 'Real-time analytics apps from the GizmoData App Factory',
        paragraphs: [
          'Vaden also builds internal analytics apps with the GizmoData App Factory, which is in early access. An employee requests an app by opening an issue in GitHub. After a platform owner approves it, the factory creates the app’s repository from a template web app with Microsoft single sign-on, sets up development and production environments, and posts the development URL back on the request.',
          'Each app connects directly to GizmoSQL, so its dashboards show live data. The factory gives each app its own GizmoSQL credentials.',
        ],
        bullets: [
          'Each app gets separate credentials for development and production. They are read-only and limited to one catalog.',
          'Credentials expire after 90 days and are rotated automatically every month.',
          'The key that signs GizmoSQL tokens is never stored in GitHub, and a person still approves each new app.',
        ],
      },
      {
        id: 'visibility',
        heading: 'Full visibility into the engine with GizmoData Cloud at Customer',
        paragraphs: [
          'Vaden\u2019s team sees how the database engine is performing in GizmoData Cloud at Customer. For each cluster, it shows CPU and memory use against the cluster\u2019s limits, along with the queries the engine runs: how many, how long they take and which ones fail.',
          'With these numbers, the team can size clusters to the actual workload and find slow queries before users notice them.',
        ],
        link: { to: '/cloud', label: 'See the GizmoData Cloud monitoring screens', cta: 'view_product' },
      },
      {
        id: 'results',
        heading: 'The results',
        paragraphs: [
          'Since moving these workloads to GizmoSQL, Vaden has seen clear improvements:',
        ],
        bullets: [
          'Faster reports: dashboards and internal apps query GizmoSQL directly and return quickly.',
          'Faster AI answers: Claude gets query results back from GizmoSQL quickly, so plain-English questions are answered faster.',
          'Dramatically lower cost: the interactive queries from people, apps and Claude run on GizmoSQL instead of the warehouse.',
          'Full visibility: GizmoData Cloud at Customer shows CPU, memory and query performance for every cluster.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What does Vaden Automotive use GizmoSQL for?',
        answer:
          'Vaden Automotive replicates data from Snowflake into GizmoSQL, which it uses as a fast SQL engine for two workloads: natural-language questions from employees through Claude, using the GizmoSQL MCP server, and real-time internal analytics apps built with the GizmoData App Factory.',
      },
      {
        question: 'Where does Vaden’s GizmoSQL deployment run?',
        answer:
          'In Vaden’s own AWS account, through GizmoData Cloud at Customer. Its control plane and the GizmoSQL clusters run on Vaden’s existing EKS cluster, and data stays in Vaden’s S3 buckets, encrypted with Vaden’s own KMS keys.',
      },
      {
        question: 'Why replicate Snowflake data into GizmoSQL instead of querying Snowflake directly?',
        answer:
          'An AI assistant and real-time dashboards send many small queries that each need a fast answer. Serving them from GizmoSQL, a DuckDB-based engine running on dedicated clusters, keeps them fast and keeps that traffic off the warehouse. Snowflake remains the system of record.',
      },
      {
        question: 'What results has Vaden Automotive seen with GizmoSQL?',
        answer:
          'Reports and AI answers through Claude are faster, and costs are dramatically lower, because the interactive queries run on GizmoSQL instead of the warehouse. GizmoData Cloud at Customer also gives the team full visibility into the engine\u2019s CPU, memory and query performance.',
      },
      {
        question: 'What is the GizmoData App Factory?',
        answer:
          'The App Factory, currently in early access, turns an approved request into a deployed internal web app. It creates the app’s repository from a template with single sign-on, sets up development and production environments, and gives the app its own read-only GizmoSQL credentials, which are rotated automatically.',
      },
    ],
    quote: {
      quote: vadenChiltonQuote.quote,
      author: vadenChiltonQuote.author,
      title: vadenChiltonQuote.title,
      linkedIn: vadenChiltonQuote.linkedIn,
      company: vadenChiltonQuote.company,
      photo: vadenChiltonQuote.photo,
      photoSize: vadenChiltonQuote.photoSize,
      ptBR: vadenChiltonQuote.ptBR,
    },
  },
];

// Slugs only — read by routes.tsx getStaticPaths so each case study prerenders.
export const caseStudySlugs = caseStudies.map((c) => c.slug);

export const getCaseStudy = (slug: string | undefined) => caseStudies.find((c) => c.slug === slug);
